// frontend/app/components/admin/useAdminCurriculum.js
//
// The curriculum tree for one board, for the create wizard's
// TopicAnchorPicker when LessonWizard is mounted from the admin Lesson
// Library (LessonLibrarySection.js).
//
// AITutorApp keeps its own `curriculum` state from GET
// /api/curriculum_with_images, but that route resolves the board from the
// *requesting student's* content_db server-side (backend/server.py ~39716) —
// an admin session has no student behind it, so it cannot ask that route for
// an arbitrary board. GET /api/curriculum (backend/server.py ~44177) builds
// the identical tree (quiz_helpers.load_curriculum: level{id, grade,
// subjects[{id, name, chapters[{id, name, topics[{id, source_id, name}]}]}]})
// for whichever board the X-Content-DB header names, and needs no per-student
// state — exactly what a board picked from a chip in the admin panel needs.
// Its response body is already the plain array (store["curriculum"]), so
// nothing here transforms it before handing it to TopicAnchorPicker.

import { useEffect, useRef, useState } from "react";

import { useAuth } from "../../AuthContext";
import { FASTAPI_BASE_URL } from "../../config";

const CURRICULUM_URL = `${FASTAPI_BASE_URL}/curriculum`;

const readError = async (res) => {
  try {
    const data = await res.json();
    const detail = data?.detail || data?.message;
    if (typeof detail === "string" && detail) return detail;
  } catch {
    // Body wasn't JSON (or already consumed) — fall through to the status line.
  }
  return `Could not load the curriculum (${res.status}).`;
};

/**
 * `{ curriculum, loading, error }` for `board`. A null/empty board fetches
 * nothing and returns `curriculum: []` — there is no "all boards" tree.
 */
const useAdminCurriculum = (board) => {
  const { fetchWithAuth } = useAuth() || {};

  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // AuthContext hands out a new `fetchWithAuth` function every render (it is
  // never memoized, in the real provider or in the mocks the admin tests
  // use). Reading it through a ref that is kept current on every render,
  // rather than through the effect's own dependency array, means that churn
  // cannot retrigger the fetch below — only an actual change of `board` can.
  // Depending on `fetchWithAuth` directly reran the effect on every render,
  // and its "no board" branch called setCurriculum([]) with a fresh array
  // each time, which is itself a state change — the two together were an
  // unbounded render loop (caught as "Maximum update depth exceeded" /
  // a worker OOM in InternalAdminPanelLessonLibrary.test.js).
  const fetchWithAuthRef = useRef(fetchWithAuth);
  fetchWithAuthRef.current = fetchWithAuth;

  useEffect(() => {
    if (!board) {
      setCurriculum([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetchWithAuthRef.current(CURRICULUM_URL, {
          headers: { "X-Content-DB": board },
        });
        if (cancelled) return;
        if (!res.ok) {
          setCurriculum([]);
          setError(await readError(res));
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setCurriculum(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setCurriculum([]);
          setError(err?.message || "Could not load the curriculum.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [board]);

  return { curriculum, loading, error };
};

export default useAdminCurriculum;
